import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useT } from "@/lib/i18n";
import { scrollToId } from "@/lib/scroll";

const rise = (delay: number) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] as const },
});

export const Hero = () => {
  const t = useT();
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-svh flex-col overflow-hidden"
    >
      <h1 className="sr-only">{t.brand.name}</h1>
      <div className="flex flex-1 items-center justify-center px-2 pt-24 sm:px-4 md:px-10 lg:px-16">
        <motion.div
          style={{ y }}
          {...rise(0.2)}
          data-testid="hero-artwork-slot"
          className="flex w-full items-center justify-center"
        >
            <img
              src="/hero-master.webp"
              alt="Istanbul Frequency — Independent Music Label. 41.0082° N · 28.9784° E. Music lives in between."
              data-testid="hero-master-image"
              draggable={false}
              className="h-auto w-[138%] max-w-none max-h-[calc(100svh-230px)] select-none object-contain md:w-full"
            />
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 1 }}
        className="flex flex-col items-center gap-7 pb-8 pt-4"
      >
        <div className="flex flex-wrap items-center justify-center gap-4 px-6">
          <button
            data-testid="hero-cta-music"
            onClick={() => scrollToId("music")}
            className="bg-if-gold px-6 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-if-black transition-colors duration-300 hover:bg-if-golddeep sm:px-8 sm:py-3 sm:text-[11px]"
          >
            {t.hero.ctaMusic}
          </button>
          <button
            data-testid="hero-cta-licensing"
            onClick={() => scrollToId("licensing")}
            className="border border-if-line px-6 py-2.5 text-[10px] uppercase tracking-[0.25em] text-if-gold transition-colors duration-300 hover:bg-if-surface sm:px-8 sm:py-3 sm:text-[11px]"
          >
            {t.hero.ctaLicensing}
          </button>
        </div>
        <div className="flex flex-col items-center gap-2">
          <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-if-dim">
            {t.hero.scroll}
          </span>
          <span className="block h-10 w-px animate-scroll-pulse bg-if-gold/50" />
        </div>
      </motion.div>
    </section>
  );
};
